// "Manage audience" modal (Phase 5A UI polish).
//
// User-friendly translation of the technical applicability model: instead of
// raw org_type/scope inputs, the admin toggles who a policy is required for —
// Resellers (buy-side) / Vendors (supply-side) — and manages Specific and
// Exempt organizations by id. On save these map to agreement_applicability rules
// (require scope=buy|supply, require org_id, exempt org_id).

import { useState } from 'react'
import { DialogContent } from '../../../components/ui/dialog'
import { Dialog } from '../../../components/ui/dialog.lib'
import { Button } from '../../../components/ui/button'
import { AgreementsError } from '../../../api/agreements'
import * as api from '../../../api/agreementsAdmin'
import type { ApplicabilityRule } from '../../../api/agreementsAdmin'
import { useT } from '../../../i18n'
import { policiesI18n } from '../../../i18n/admin/policies'
import { audienceModalI18n } from '../../../i18n/admin/audience-modal'

export function AudienceModal({
  agreementId,
  existing,
  onClose,
  onSaved,
}: {
  agreementId: string
  existing: ApplicabilityRule[]
  onClose: () => void
  onSaved: () => void | Promise<void>
}) {
  const t = useT(policiesI18n)
  const [resellers, setResellers] = useState(
    existing.some((r) => r.effect === 'require' && r.scope === 'buy'),
  )
  const [vendors, setVendors] = useState(
    existing.some((r) => r.effect === 'require' && r.scope === 'supply'),
  )
  const [specific, setSpecific] = useState<string[]>(
    existing.filter((r) => r.effect === 'require' && r.org_id).map((r) => r.org_id as string),
  )
  const [exempt, setExempt] = useState<string[]>(
    existing.filter((r) => r.effect === 'exempt' && r.org_id).map((r) => r.org_id as string),
  )
  const [specificInput, setSpecificInput] = useState('')
  const [exemptInput, setExemptInput] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function addTo(list: string[], set: (v: string[]) => void, value: string) {
    const v = value.trim()
    if (v && !list.includes(v)) set([...list, v])
  }

  async function save() {
    setBusy(true)
    setError(null)
    try {
      const rules: ApplicabilityRule[] = []
      if (resellers) rules.push({ effect: 'require', scope: 'buy' })
      if (vendors) rules.push({ effect: 'require', scope: 'supply' })
      for (const org of specific) rules.push({ effect: 'require', org_id: org })
      for (const org of exempt) rules.push({ effect: 'exempt', org_id: org })
      await api.setApplicability(agreementId, rules)
      await onSaved()
      onClose()
    } catch (e) {
      setError(e instanceof AgreementsError ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-full max-w-md gap-0 p-0">
        <div className="border-b border-slate-100 px-5 py-4">
          <h2 className="text-base font-semibold text-slate-900">{t.manageAudience}</h2>
          <p className="mt-0.5 text-xs text-slate-500">{t.audienceHint}</p>
        </div>

        <div className="space-y-4 px-5 py-4">
          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {t.requiredFor}
            </p>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={resellers}
                onChange={(e) => setResellers(e.target.checked)}
                data-testid="audience-resellers"
              />
              {t.resellers}
              <span className="text-xs text-slate-400">{t.resellersHint}</span>
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={vendors}
                onChange={(e) => setVendors(e.target.checked)}
                data-testid="audience-vendors"
              />
              {t.vendors}
              <span className="text-xs text-slate-400">{t.vendorsHint}</span>
            </label>
          </div>

          <ChipField
            label={t.specificOrgs}
            hint={t.specificOrgsHint}
            values={specific}
            input={specificInput}
            setInput={setSpecificInput}
            onAdd={() => {
              addTo(specific, setSpecific, specificInput)
              setSpecificInput('')
            }}
            onRemove={(v) => setSpecific(specific.filter((x) => x !== v))}
            testid="specific"
          />
          <ChipField
            label={t.exemptOrgs}
            hint={t.exemptOrgsHint}
            values={exempt}
            input={exemptInput}
            setInput={setExemptInput}
            onAdd={() => {
              addTo(exempt, setExempt, exemptInput)
              setExemptInput('')
            }}
            onRemove={(v) => setExempt(exempt.filter((x) => x !== v))}
            testid="exempt"
          />
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-100 bg-slate-50 px-5 py-3">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>
            {t.cancel}
          </Button>
          <Button size="sm" onClick={save} disabled={busy} data-testid="audience-save">
            {t.save}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function ChipField({
  label,
  hint,
  values,
  input,
  setInput,
  onAdd,
  onRemove,
  testid,
}: {
  label: string
  hint: string
  values: string[]
  input: string
  setInput: (v: string) => void
  onAdd: () => void
  onRemove: (v: string) => void
  testid: string
}) {
  const tAudience = useT(audienceModalI18n)
  return (
    <div className="space-y-1.5">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="text-xs text-slate-400">{hint}</p>
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {values.map((v) => (
            <span
              key={v}
              className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700"
            >
              {v}
              <button
                type="button"
                onClick={() => onRemove(v)}
                className="text-slate-400 hover:text-slate-600"
                aria-label={`Remove ${v}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="flex gap-1.5">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              onAdd()
            }
          }}
          placeholder={tAudience.orgIdPlaceholder}
          className="flex-1 rounded border border-slate-300 px-2 py-1 text-sm"
          data-testid={`audience-${testid}-input`}
        />
        <Button type="button" variant="outline" size="sm" onClick={onAdd}>
          +
        </Button>
      </div>
    </div>
  )
}
